import React, { useEffect, useState, useRef } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import 'dayjs/locale/en-gb'

import Button from '/react4xp/components/Dashboard/Button'
import Checkbox from '/react4xp/components/Dashboard/Checkbox'
import Accordion from '/react4xp/components/Dashboard/Accordion'
import Input from '/react4xp/components/Dashboard/Input'
import Popup from '/react4xp/components/Dashboard/Popup'
import TextArea from '/react4xp/components/Dashboard/TextArea'
import Image from '/react4xp/components/Image'
import Select from '/react4xp/components/Dashboard/Select'
import RichText from '/react4xp/components/RichText'

import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { nnNO } from '@mui/material/locale'

import { limitCharacters, useDomLoaded } from '/lib/reactutils/utils'

interface DashboardListProps {
    filters: {
        status: { id: string, text: string }[]
        packaging: { id: string, text: string }[]
        taste?: { id: string, text: string }[]
        category: { id: string, text: string }[]
        region?: { id: string, text: string }[]
        justShow?: { id: string, text: string }[]
        ingredients1Label?: string
        ingredients1?: { id: string, text: string }[]
        ingredients2Label?: string
        ingredients2?: { id: string, text: string }[]
        ingredients3Label?: string
        ingredients3?: { id: string, text: string }[]
    }
    endpoints: {
        saveSearch: string
        manage: string
        websocket: string
        aiReply: string
        unreport?: string
    }
    strings: {
        saveSearchButton: string//Lagre søk
        saveSearchCreateHeading: string//Lagre søk
        saveSearchNameInputLabel: string//Navn
        saveSearchCancelButton: string//Avbryt
        saveSearchConfirmButton: string//Lagre

        saveSearchSuccessTitle: string//Success
        saveSearchSuccessContent: string//The search was created successfully
        saveSearchErrorTitle: string//Success
        saveSearchErrorContent: string//The search was created successfully

        editSearchButton: string//Endre
        editSearchTitle: string//Endre
        editSearchInputText: string//Navn
        editSearchConfirm: string//Lagre
        editSearchCancel: string//Avbryt

        popupErrorTitle: string//Error
        popupErrorContent: string//An error occurred during the action

        popupSuccessTitle: string//Success
        popupSuccessContent: string//The action was executed successfully

        deleteSearchTitle: string//Ekskluder dette søket
        deleteSearchContent: string//Er du sikker på at du vil slette dette lagrede søket?
        deleteSearchConfirm: string//Ja
        deleteSearchCancel: string//Avbryt

        popupModalClose: string//Lukk

        saveSearchInputPlaceholder: string //Bringebær & lakris
        saveSearchShowAll: string //Vis lagrede søk
        saveSearchHeader: string //Lagrede søk
        clearAllFilters: string //Tøm alle filtre
        justShowFilterHeader: string //Vis bare
        dateFilterFrom: string //Fra
        dateFilterTo: string //Til
        dateFilterMaxHigherThanToday: string //Du må velge en dato som er mindre enn eller lik i dag
        statusFilterHeader: string //Status
        packagingFilterHeader: string //Innpakning
        tasteFilterHeader: string //Smak
        categoryFilterHeader: string //Kategori
        regionFilterHeader: string //Område
        chooseOne: string //Velg

        selectAll: string //Velg alle
        unselectAll: string //Slett alle valgte
        editContent: string //Rediger
        detailContent: string //Detaljer
        saveButton: string //Lagre
        paginationOf: string //av
        excludePopupTitle: string //Vil du slette denne ideen?
        excludePopupContent: string //Er du sikker på at du vil slette denne ideen?
        excludePopupConfirm: string //Ja
        excludePopupCancel: string //Avbryt
        phoneLabel: string //Tlf
        publishButton: string //Publiser
        publishAndSendButton: string //Publiser og send
        isEditingLabel: string //Redigeres for øyeblikket
        isAnsweredLabel: string //Besvart
        isReportedLabel: string //Rapportert
        unreportButton: string //Fjern rapportering

        dateFilterClear: string //Klar

        unlockWsButton: string//Unlock editing
        unlockWsPopupTitle: string//Unlock editing
        unlockWsPopupContent: string//Do you want to unlock all locked contents?
        unlockWsPopupConfirm: string//Yes
        unlockWsPopupCancel: string//Cancel

        searchedTerm: string//Searched term
    }
    assets: {
        noPhoto: string
    },
    type: string
}

export default function DashboardList({
    filters,
    endpoints,
    strings,
    assets,
    type
}: DashboardListProps) {
    const domLoaded = useDomLoaded()

    const dateStart = useRef(null)
    const dateEnd = useRef(null)

    // handle ideas
    const [ideas, setIdeas] = useState([])
    const [filterDateStart, setFilterDateStart] = useState(null)
    const [filterDateEnd, setFilterDateEnd] = useState(null)
    const [editSearch, setEditSearch] = useState(null)
    const [inputValue, setInputValue] = useState('')
    const [searchQuery, setSearchQuery] = useState(null)
    const [forceSearchVar, setForceSearchVar] = useState(false)
    const [isDomReady, setIsDomReady] = useState(false)

    // state to track selected filters
    const [activeFilters, setActiveFilters] = useState([])
    const activeFiltersRef = useRef(activeFilters)

    const getQueryParams = () => {
        const queryParams = new URLSearchParams()

        const currentParams = window.location.search && new URLSearchParams(window.location.search)
        if (currentParams && currentParams.get('id')) {
            queryParams.append('id', currentParams.get('id'))
        }

        (activeFiltersRef.current || []).forEach(af => {
            if (af.name == 'status') queryParams.append('status', af.id)
            if (af.name == 'packaging') queryParams.append('packaging', af.id)
            if (af.name == 'taste') queryParams.append('taste', af.id)
            if (af.name == 'category') queryParams.append('category', af.id)
            if (af.name == 'region') queryParams.append('region', af.id)
            if (af.name == 'just-show') queryParams.append('just-show', af.id)

            if (af.name == 'ingredient1') queryParams.append('ingredient', af.id)
            if (af.name == 'ingredient2') queryParams.append('ingredient', af.id)
            if (af.name == 'ingredient3') queryParams.append('ingredient', af.id)
        })

        if (filterDateStart?.$d) queryParams.append('dateStart', dayjs(filterDateStart.$d.toISOString()).set('hour', 0).set('minute', 0).set('second', 0).format('YYYY-MM-DDTHH:mm:ss'))
        if (filterDateEnd?.$d) queryParams.append('dateEnd', dayjs(filterDateEnd.$d.toISOString()).set('hour', 23).set('minute', 59).set('second', 59).format('YYYY-MM-DDTHH:mm:ss'))

        if (searchQuery) queryParams.append('search', searchQuery)

        return queryParams
    }

    // pagination
    const itemsPerPage = 10
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const currentPageRef = useRef(currentPage)

    const handlePageClick = (page) => {
        setIdeas([])
        setCurrentPage(page)
        setSelectedIdeas([])
    }

    useEffect(() => {
        setIsDomReady(true)
    }, [])

    useEffect(() => {
        activeFiltersRef.current = activeFilters
        const lastCurrentPage = currentPage
        setCurrentPage(1)

        if (lastCurrentPage == 1 && isDomReady) {
            handleContents()
        }
    }, [activeFilters, filterDateStart, filterDateEnd, forceSearchVar])

    useEffect(() => {
        currentPageRef.current = currentPage
        if (currentPage != 1) {
            handleContents()
        }
    }, [currentPage])

    function forceSearch() {
        setForceSearchVar(f => !f)
    }

    const handleContents = async (editedIds?, deletedIds?) => {
        if (!endpoints.manage) return

        const url = new URL(endpoints.manage)
        const queryParams = getQueryParams()

        queryParams.append('pageNumber', currentPageRef?.current?.toString() || '1')

        url.search = queryParams.toString()

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`)
            }
            const data = await response.json()

            if (editedIds || deletedIds) {
                setIdeas(prevIdeas => {
                    const updatedIdeas = [...prevIdeas];

                    if (editedIds) {
                        data.hits.forEach(hit => {
                            if (editedIds.includes(hit.id)) {
                                const index = updatedIdeas.findIndex(idea => idea.id === hit.id);
                                if (index !== -1) {
                                    updatedIdeas[index] = hit;
                                } else {
                                    updatedIdeas.push(hit);
                                }
                            }
                        });

                        // Remove ideas that are in editedIds but not in data.hits
                        const dataHitsIds = data.hits.map(hit => hit.id);
                        editedIds.forEach(editedId => {
                            if (!dataHitsIds.includes(editedId)) {
                                const index = updatedIdeas.findIndex(idea => idea.id === editedId);
                                if (index !== -1) {
                                    updatedIdeas.splice(index, 1);
                                }
                            }
                        });
                    }

                    if (deletedIds) {
                        deletedIds.forEach(id => {
                            const index = updatedIdeas.findIndex(idea => idea.id === id);
                            if (index !== -1) {
                                updatedIdeas.splice(index, 1);
                            }
                        });
                    }

                    return updatedIdeas;
                });
            } else {
                setIdeas(data.hits || []);
            }
            setTotalPages(data.totalPages)
            setTotalItems(data.total)

        } catch (error) {
            console.error('There was a problem with the fetch operation:', error)
        }
    }

    // handle checkbox changes
    const handleCheckboxChange = (id: string, text: string, name: string, checked: boolean) => {
        setActiveFilters(prev => checked
            ? [...prev, { id, text, name }]
            : prev.filter(f => !(f.id === id && f.name === name))
        )

        const url = new URL(window.location.href)
        const params = url.searchParams
        let currentValue = params.get(name) || ''

        if (checked) {
            let values = currentValue ? currentValue.split(',') : []
            if (!values.includes(id)) {
                values.push(id)
                params.set(name, values.join(','))
            }
        } else {
            let values = currentValue ? currentValue.split(',') : []
            values = values.filter(val => val !== id)
            if (values.length > 0) {
                params.set(name, values.join(','))
            } else {
                params.delete(name)
            }
        }

        window.history.replaceState({}, '', `${url.pathname}?${params.toString()}`)
    }

    // clear a specific filter
    const clearFilter = (id: string, name: string) => {

        // remove only the matching filter (by both id and name/dimension)
        setActiveFilters((prevFilters) =>
            prevFilters.filter((filter) => !(filter.id === id && filter.name === name))
        )

        // uncheck the corresponding checkbox using the dimension-prefixed DOM id
        const PREFIXED_NAMES = ['status', 'packaging', 'taste', 'category', 'region', 'just-show']
        const domId = PREFIXED_NAMES.includes(name) ? `${name}-${id}` : id
        const checkbox = document.getElementById(domId) as HTMLInputElement
        if (checkbox) checkbox.checked = false

        // sync the URL: remove this specific value from the correct URL param
        const url = new URL(window.location.href)
        const params = url.searchParams
        const urlParamName = (name === 'ingredient1' || name === 'ingredient2' || name === 'ingredient3') ? 'ingredient' : name
        let values = (params.get(urlParamName) || '').split(',').filter(Boolean)
        values = values.filter(val => val !== id)
        if (values.length > 0) {
            params.set(urlParamName, values.join(','))
        } else {
            params.delete(urlParamName)
        }
        window.history.replaceState({}, '', `${url.pathname}?${params.toString()}`)
    }

    const clearActiveFilters = () => {
        const PREFIXED_NAMES = ['status', 'packaging', 'taste', 'category', 'region', 'just-show']
        activeFilters.forEach(af => {
            const domId = PREFIXED_NAMES.includes(af.name) ? `${af.name}-${af.id}` : af.id
            const checkbox = document.getElementById(domId) as HTMLInputElement
            if (checkbox) {
                checkbox.checked = false
            }
        })

        setActiveFilters([])
    }

    // show / hide the search bar saved searches
    const [searchFocus, setSearchFocus] = useState(false)

    // open popups
    useEffect(() => {
        const dialogTriggers = document.querySelectorAll('[data-dialog]')

        dialogTriggers.forEach(trigger => {
            trigger.addEventListener('click', (event) => {
                event.preventDefault()

                document.body.classList.add('no-scroll')

                const dialogId = trigger.getAttribute('href').substring(1)
                const dialog = document.getElementById(dialogId)

                if (dialog && (dialog as HTMLDialogElement).showModal) {
                    (dialog as HTMLDialogElement).showModal()
                }
            })
        })
    }, [])

    // Save search
    const [searches, setSearches] = useState([])
    const [saveSearchLabel, setSaveSearchLabel] = useState('')
    const handleSaveSearch = () => {
        fetch(endpoints.saveSearch, {
            method: 'post',
            body: JSON.stringify({
                label: saveSearchLabel,
                filters: activeFilters,
                searchQuery: searchQuery
            })
        })
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to create search.')
                }
            })
            .then(responseData => {
                console.log('Save search created')
                handleGetSearches()
            })
            .catch(error => {
                showErrorModal()
            })
    }

    const [errorModal, setErrorModal]: any = useState({})
    const showErrorModal = (title?, message?) => {
        const element = document.getElementById('dashboard-error-modal') as HTMLDialogElement
        setErrorModal({
            title: title || strings.popupErrorTitle,
            message: message || strings.popupErrorContent
        })
        element.showModal()
    }

    const [successModal, setSuccessModal]: any = useState({})
    const showSuccessModal = (onCloseFn?, title?, message?) => {
        const element = document.getElementById('dashboard-success-modal') as HTMLDialogElement
        setSuccessModal({
            title: title || strings.popupSuccessTitle,
            message: message || strings.popupSuccessContent,
            onCloseFn: onCloseFn
        })
        element.showModal()
    }

    const handleEditSearch = () => {
        fetch(`${endpoints.saveSearch}&id=${editSearch?.id}`, {
            method: 'put',
            body: JSON.stringify({
                label: editSearch?.label,
                filters: editSearch?.filters,
                searchQuery: editSearch?.searchQuery
            })
        })
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to edit search.')
                }
            })
            .then(responseData => {
                handleGetSearches()
            })
            .catch(error => {
                showErrorModal()
            })
    }

    const handleGetSearches = () => {
        fetch(endpoints.saveSearch)
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to get searches.')
                }
            })
            .then(responseData => {
                setSearches(responseData.contents)
            })
            .catch(error => {
                console.error('Fail while get searches')
            })
    }

    const handleSetSearch = (filters) => {
        clearActiveFilters()
        const PREFIXED_NAMES = ['status', 'packaging', 'taste', 'category', 'region', 'just-show']
        filters.forEach(f => {
            const domId = PREFIXED_NAMES.includes(f.name) ? `${f.name}-${f.id}` : f.id
            const checkbox = document.getElementById(domId) as HTMLInputElement
            if (checkbox) {
                checkbox.checked = true
            }
        })
        setActiveFilters(filters)
    }

    const handleDeleteSearch = (id) => {
        fetch(`${endpoints.saveSearch}&id=${id}`, {
            method: 'delete'
        })
            .then(response => {
                if (response.ok) {
                    return response
                } else {
                    throw new Error('Failed to delete search.')
                }
            })
            .then(response => {
                handleGetSearches()
            })
            .catch(error => {
                showErrorModal()
            })
    }

    // Set initial params from the URL in the state
    useEffect(() => {
        const url = new URL(window.location.href)
        const params = url.searchParams
        const newActiveFilters: { id: string; text: string; name: string }[] = []

        params.forEach((value, key) => {
            if (value && key) {
                if (key === 'dateStart') {
                    setFilterDateStart(dayjs(value))
                } else if (key === 'dateEnd') {
                    setFilterDateEnd(dayjs(value))
                } else {
                    const PREFIXED_NAMES = ['status', 'packaging', 'taste', 'category', 'region', 'just-show']
                    const splittedValues = value.split(',')
                    splittedValues.forEach(splittedValueItem => {
                        const domId = PREFIXED_NAMES.includes(key) ? `${key}-${splittedValueItem}` : splittedValueItem
                        const checkbox = document.getElementById(domId) as HTMLInputElement
                        if (checkbox) checkbox.checked = true
                        newActiveFilters.push({
                            id: splittedValueItem,
                            text: splittedValueItem,
                            name: key
                        })
                    })
                }
            }
        })

        setActiveFilters(newActiveFilters)
        handleGetSearches()
    }, [])

    // show / hide the top buttons when an idea is checked
    const [selectedIdeas, setSelectedIdeas] = useState([])
    const selectedIdeasRef = useRef([])

    useEffect(() => {
        selectedIdeasRef.current = selectedIdeas
    }, [selectedIdeas])

    const handleIdeaSelection = (idea, isChecked) => {
        if (isChecked) {
            // ddd the idea's ID to the selectedIdeas array
            setSelectedIdeas((prevSelected) => [...prevSelected, idea])
        } else {
            // remove the idea's ID from the selectedIdeas array
            setSelectedIdeas((prevSelected) =>
                prevSelected.filter((prevIdea) => prevIdea.id !== idea.id)
            )
        }
    }

    // check if any idea is selected
    const ideaSelected = selectedIdeas.length > 0

    // select all ideas (expect the disabled ones)
    const handleSelectAll = () => {
        const allSelectableIdeas = ideas
            .filter((idea) => (inEditionContents.indexOf(idea.id) == -1))

        setSelectedIdeas(allSelectableIdeas)
    }

    // unselect all ideas
    const handleUnselectAll = () => {
        setSelectedIdeas([])
    }

    const handleEditIdea = (items, isSendEmail?) => {
        const url = new URL(endpoints.manage)

        const queryParams = new URLSearchParams()
        if (isSendEmail) {
            queryParams.append('isSendEmail', 'true')
        }

        const currentParams = window.location.search && new URLSearchParams(window.location.search)
        if (currentParams && currentParams.get('id')) {
            queryParams.append('id', currentParams.get('id'))
        }

        if (queryParams.size > 0) {
            url.search = queryParams.toString()
        }

        fetch(url, {
            method: 'put',
            body: JSON.stringify(items)
        })
            .then(response => {
                sendWsMessage('handle_action', Array.isArray(items) ? items.map(item => item.id) : [items.id]);
                if (response.ok) {
                    return response
                } else {
                    throw new Error('Failed to create search.')
                }
            })
            .then(responseData => {
                showSuccessModal()
            })
            .catch(error => {
                showErrorModal()
            })
    }

    const handleUnreportIdea = (item) => {
        if (!endpoints.unreport) return
        const url = `${endpoints.unreport}?contentId=${encodeURIComponent(item.id)}`
        fetch(url, {
            method: 'POST'
        })
            .then(response => {
                sendWsMessage('handle_action', [item.id])
                if (response.ok) {
                    return response
                } else {
                    throw new Error('Failed to un-report.')
                }
            })
            .then(() => {
                showSuccessModal(() => handleContents())
            })
            .catch(() => {
                showErrorModal()
            })
    }

    const handleDeleteIdeaImage = (item) => {
        const url = new URL(endpoints.manage)
        const queryParams = new URLSearchParams()
        queryParams.append('isDeleteImage', 'true')
        url.search = queryParams.toString()

        fetch(url, {
            method: 'put',
            body: JSON.stringify(item)
        })
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to create search.')
                }
            })
            .then(responseData => {
                handleContents()
            })
            .catch(error => {
                showErrorModal()
            })
    }

    const handleDeleteIdeas = (ids) => {
        const url = new URL(endpoints.manage)

        const queryParams = new URLSearchParams()
        ids.forEach(id => {
            queryParams.append('contentId', id)
        })
        url.search = queryParams.toString()

        fetch(url, {
            method: 'delete'
        })
            .then(response => {
                sendWsMessage('handle_action', ids, { isDelete: true });
                if (response.ok) {
                    return response
                } else {
                    throw new Error('Failed to create search.')
                }
            })
            .then(responseData => {
                showSuccessModal(() => {
                    if (!ideas || (ideas.length <= 1)) {
                        window.location.reload()
                    }
                })

            })
            .catch(error => {
                showErrorModal()
            })
    }

    // Websocket handle
    const [inEditionContents, setInEditionContents] = useState([])
    const [ws, setWs] = useState(null)
    useEffect(() => {
        // Websocket connection
        const websocket = new WebSocket(endpoints.websocket);

        websocket.onopen = () => {
            console.log('Websocket connected');
        };

        websocket.onmessage = (message) => {
            try {
                const messageData = JSON.parse(message.data)

                if (messageData.isRefresh) {
                    if (messageData.isDelete) {
                        handleContents(null, messageData.messageContents)
                    } else {
                        handleContents(messageData.messageContents)
                    }
                }

                setInEditionContents(messageData.contents || [])
            } catch (err) {
                console.error('Fail while reading ws message')
            }
        };

        websocket.onclose = () => {
            console.log('Websocket disconnected');
        };

        setWs(websocket);

        setInterval(() => {
            // Send Ping message
            websocket.send(JSON.stringify({
                type: 'ping'
            }));
        }, 30000)

        return () => {
            websocket.close();
        };
    }, []);

    const sendWsMessage = (type: 'add' | 'remove' | 'handle_action' | 'ping' | 'clear_all', contentIds?, extraParams?) => {
        if (ws && (ws.readyState == 1)) {
            // Send WS message
            ws.send(JSON.stringify({
                type: type,
                contents: contentIds,
                isDelete: extraParams?.isDelete || false
            }));
        }
    };

    const getVisiblePages = (currentPage, totalPages) => {
        const pages = []

        // show the first block of pages (1-5)
        if (currentPage <= 3) {
            for (let i = 1; i <= Math.min(5, totalPages); i++) {
                pages.push(i)
            }

            // add ellipsis if there are pages after the first block
            if (totalPages > 5) {
                pages.push('...')
                pages.push(totalPages)
            }
        } else if (currentPage > totalPages - 3) {
            pages.push(1)
            pages.push('...')

            for (let i = totalPages - 4; i <= totalPages; i++) {
                pages.push(i)
            }
        } else {
            pages.push(1)

            const start = Math.max(2, currentPage - 2)
            const end = Math.min(currentPage + 2, totalPages - 1)

            // add ellipsis if there's a gap between 1 and start
            if (start > 2) {
                pages.push('...')
            }

            for (let i = start; i <= end; i++) {
                pages.push(i)
            }

            // add ellipsis if there's a gap between the end and the last page
            if (end < totalPages - 1) {
                pages.push('...')
            }

            // always show the last page
            pages.push(totalPages)
        }

        return pages
    }

    const handleDateChange = (e: any, startOrEnd: 'start' | 'end') => {
        const url = new URL(window.location.href)
        const params = url.searchParams

        const date = e ? dayjs(e?.$d.toISOString()).set('hour', 0).set('minute', 0).set('second', 0) : null
        if (startOrEnd === 'start') {
            date ? params.set('dateStart', date.format('YYYY-MM-DDTHH:mm:ss')) : params.delete('dateStart')
            setFilterDateStart(date)
        } else {
            date ? params.set('dateEnd', date.format('YYYY-MM-DDTHH:mm:ss')) : params.delete('dateEnd')
            setFilterDateEnd(date)
        }

        window.history.replaceState({}, '', `${url.pathname}?${params.toString()}`)
    }

    const [aiReplySuggestions, setAiReplySuggestions] = useState([])
    const [aiReplySelectedSuggestion, setAiReplySelectedSuggestion] = useState(null)
    const [aiReplyIsLoading, setAiReplyIsLoading] = useState(false)
    const [aiReplyUseAllAnswers, setAiReplyUseAllAnswers] = useState(false)
    const handleAiReply = (useAllAnswers: boolean, itemId: string) => {
        setAiReplyIsLoading(true)
        fetch(`${endpoints.aiReply}`, {
            method: 'POST',
            body: JSON.stringify({
                useAllAnswers,
                type: type,
                previousAnswers: aiReplySuggestions || [],
                currentIdeaId: itemId
            })
        })
            .then(response => response.json())
            .then(data => {
                setAiReplySuggestions((data?.suggestions || []).concat(aiReplySuggestions))
            })
            .catch(error => {
                console.error('Error fetching AI reply suggestions:', error)
            })
            .finally(() => {
                setAiReplyIsLoading(false)
            })
    }

    return (
        <main className='page-dashboard__main'>
            <div className='dashboard-grid-container'>

                <div className='dashboard-grid-container__left'>
                    <div className='sidebar'>

                        <Button
                            style='solid'
                            size='medium'
                            className='save-search'
                            onClick={() => {
                                (document.getElementById('save-search-textarea') as HTMLTextAreaElement).value = '';
                                (document.getElementById('lagre-sok') as HTMLDialogElement).showModal()
                            }}
                        >
                            {strings.saveSearchButton}
                        </Button>

                        <Popup id='lagre-sok' title={strings.saveSearchButton}>

                            <p className='dash-big'>
                                {strings.saveSearchCreateHeading}
                            </p>

                            <TextArea
                                id='save-search-textarea'
                                name={saveSearchLabel}
                                placeholder={strings.saveSearchInputPlaceholder}
                                onChange={(e) => setSaveSearchLabel(e?.target?.value || '')}
                            />

                            {inputValue && (
                                <>
                                    <p className='dash-big'>
                                        {strings.searchedTerm}
                                    </p>

                                    <Input
                                        type='text'
                                        id='search-query'
                                        name='Searched term'
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </>
                            )}

                            <div className='filters'>
                                {activeFilters.map((filter, index) => (
                                    <button
                                        key={index}
                                        className='dash-tag'
                                        onClick={() => clearFilter(filter.id, filter.name)}
                                    >

                                        <span>
                                            {filter.text}
                                        </span>

                                        <span className='clear'>
                                            <svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M0.514695 0.514726C0.905219 0.124201 1.53838 0.124201 1.92891 0.514726L8.99998 7.5858L16.0711 0.514726C16.4616 0.124201 17.0947 0.124201 17.4853 0.514726C17.8758 0.90525 17.8758 1.53841 17.4853 1.92894L10.4142 9.00001L17.4853 16.0711C17.8758 16.4616 17.8758 17.0948 17.4853 17.4853C17.0947 17.8758 16.4616 17.8758 16.071 17.4853L8.99998 10.4142L1.92892 17.4853C1.5384 17.8758 0.905233 17.8758 0.514708 17.4853C0.124184 17.0948 0.124184 16.4616 0.514708 16.0711L7.58577 9.00001L0.514695 1.92894C0.124171 1.53841 0.124171 0.90525 0.514695 0.514726Z'></path></svg>
                                        </span>

                                    </button>
                                ))}
                            </div>

                            <div className='bottom-buttons'>

                                <Button
                                    style='hollow'
                                    size='medium'
                                    data-dialog-close
                                >
                                    {strings.saveSearchCancelButton}
                                </Button>

                                <Button
                                    style='solid'
                                    size='medium'
                                    data-dialog-close
                                    onClick={() => {
                                        handleSaveSearch()
                                    }}
                                >
                                    {strings.saveSearchConfirmButton}
                                </Button>

                            </div>

                        </Popup>

                        <div className={clsx(
                            'search-bar',
                            searchFocus && 'search-bar--has-focus'
                        )}>

                            <div className='search-bar-wrapper'>

                                <Input
                                    type='text'
                                    id='dash-search-input'
                                    name='Search'
                                    onFocus={() => setSearchFocus(true)}
                                    onBlur={() => setSearchFocus(false)}
                                    value={inputValue}
                                    onChange={(e) => {
                                        setInputValue(e.target.value)
                                        setSearchQuery(e.target.value)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            forceSearch()
                                        }
                                    }}
                                />

                                <button className='button--search' onClick={() => forceSearch()}>
                                    <svg width='23' height='23' viewBox='0 0 23 23' xmlns='http://www.w3.org/2000/svg'><path d='M6.57043 1.24854C7.76349 0.754353 9.04221 0.5 10.3336 0.5C11.6249 0.5 12.9037 0.754353 14.0967 1.24854C15.2898 1.74272 16.3738 2.46706 17.287 3.38019C18.2001 4.29332 18.9244 5.37736 19.4186 6.57043C19.9128 7.76349 20.1671 9.04221 20.1671 10.3336C20.1671 11.6249 19.9128 12.9037 19.4186 14.0967C18.9883 15.1356 18.3835 16.0917 17.6315 16.9244L22.3538 21.6467C22.5491 21.8419 22.5491 22.1585 22.3538 22.3538C22.1585 22.5491 21.8419 22.5491 21.6467 22.3538L16.9244 17.6315C15.1218 19.2597 12.7744 20.1671 10.3336 20.1671C7.72555 20.1671 5.22434 19.1311 3.38019 17.287C1.53603 15.4428 0.5 12.9416 0.5 10.3336C0.5 7.72555 1.53603 5.22434 3.38019 3.38019C4.29332 2.46706 5.37736 1.74272 6.57043 1.24854ZM10.3336 1.5C9.17353 1.5 8.02485 1.72849 6.95311 2.17242C5.88137 2.61634 4.90757 3.26702 4.08729 4.08729C2.43068 5.74391 1.5 7.99076 1.5 10.3336C1.5 12.6764 2.43068 14.9232 4.08729 16.5799C5.74391 18.2365 7.99076 19.1671 10.3336 19.1671C12.6764 19.1671 14.9232 18.2365 16.5799 16.5799C17.4001 15.7596 18.0508 14.7858 18.4947 13.714C18.9387 12.6423 19.1671 11.4936 19.1671 10.3336C19.1671 9.17353 18.9387 8.02485 18.4947 6.95311C18.0508 5.88137 17.4001 4.90757 16.5799 4.08729C15.7596 3.26702 14.7858 2.61634 13.714 2.17242C12.6423 1.72849 11.4936 1.5 10.3336 1.5Z' /></svg>
                                </button>

                            </div>

                            <div className='search-suggestions text-smaller'>
                                <div className='wrapper'>

                                    <ul className='list'>
                                        {(searches || []).map((item, i) => (
                                            <li key={i}>
                                                <button onClick={() => {
                                                    setSearchQuery(item.searchQuery || '')
                                                    setInputValue(item.searchQuery)
                                                    handleSetSearch(item.filters)
                                                    forceSearch()
                                                }}>
                                                    <span className='circle'></span>
                                                    <span>{item.label}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    <a href='#lagrede-sok' data-dialog className='save-search'>
                                        <b>
                                            <u>
                                                {strings.saveSearchShowAll}
                                            </u>
                                        </b>
                                    </a>

                                    <Popup id='lagrede-sok' title={strings.saveSearchHeader}>
                                        <div className='inner-scroll'>
                                            {searches.map((item, i) => (
                                                <div className='saved-item' key={i}>

                                                    <div className='saved-item__top'>

                                                        <h3 className='h4'>
                                                            {item.label}
                                                        </h3>

                                                        <button
                                                            className='exclude'
                                                            onClick={() => (document.getElementById('exclude-' + i) as HTMLDialogElement).showModal()}
                                                        >
                                                            <svg width='22' height='24' viewBox='0 0 22 24' xmlns='http://www.w3.org/2000/svg'><path d='M21.3125 4.09375H16.1562V2.6875C16.1562 2.0659 15.9093 1.46976 15.4698 1.03022C15.0302 0.59068 14.4341 0.34375 13.8125 0.34375H8.1875C7.5659 0.34375 6.96976 0.59068 6.53022 1.03022C6.09068 1.46976 5.84375 2.0659 5.84375 2.6875V4.09375H0.6875C0.56318 4.09375 0.443951 4.14314 0.356044 4.23104C0.268136 4.31895 0.21875 4.43818 0.21875 4.5625C0.21875 4.68682 0.268136 4.80605 0.356044 4.89396C0.443951 4.98186 0.56318 5.03125 0.6875 5.03125H2.09375V22.375C2.09375 22.748 2.24191 23.1056 2.50563 23.3694C2.76935 23.6331 3.12704 23.7812 3.5 23.7812H18.5C18.873 23.7812 19.2306 23.6331 19.4944 23.3694C19.7581 23.1056 19.9062 22.748 19.9062 22.375V5.03125H21.3125C21.4368 5.03125 21.556 4.98186 21.644 4.89396C21.7319 4.80605 21.7812 4.68682 21.7812 4.5625C21.7812 4.43818 21.7319 4.31895 21.644 4.23104C21.556 4.14314 21.4368 4.09375 21.3125 4.09375ZM6.78125 2.6875C6.78125 2.31454 6.92941 1.95685 7.19313 1.69313C7.45685 1.42941 7.81454 1.28125 8.1875 1.28125H13.8125C14.1855 1.28125 14.5431 1.42941 14.8069 1.69313C15.0706 1.95685 15.2188 2.31454 15.2188 2.6875V4.09375H6.78125V2.6875ZM18.9688 22.375C18.9688 22.4993 18.9194 22.6185 18.8315 22.7065C18.7435 22.7944 18.6243 22.8438 18.5 22.8438H3.5C3.37568 22.8438 3.25645 22.7944 3.16854 22.7065C3.08064 22.6185 3.03125 22.4993 3.03125 22.375V5.03125H18.9688V22.375ZM8.65625 10.1875V17.6875C8.65625 17.8118 8.60686 17.931 8.51896 18.019C8.43105 18.1069 8.31182 18.1562 8.1875 18.1562C8.06318 18.1562 7.94395 18.1069 7.85604 18.019C7.76814 17.931 7.71875 17.8118 7.71875 17.6875V10.1875C7.71875 10.0632 7.76814 9.94395 7.85604 9.85604C7.94395 9.76814 8.06318 9.71875 8.1875 9.71875C8.31182 9.71875 8.43105 9.76814 8.51896 9.85604C8.60686 9.94395 8.65625 10.0632 8.65625 10.1875ZM14.2812 10.1875V17.6875C14.2812 17.8118 14.2319 17.931 14.144 18.019C14.056 18.1069 13.9368 18.1562 13.8125 18.1562C13.6882 18.1562 13.569 18.1069 13.481 18.019C13.3931 17.931 13.3438 17.8118 13.3438 17.6875V10.1875C13.3438 10.0632 13.3931 9.94395 13.481 9.85604C13.569 9.76814 13.6882 9.71875 13.8125 9.71875C13.9368 9.71875 14.056 9.76814 14.144 9.85604C14.2319 9.94395 14.2812 10.0632 14.2812 10.1875Z' /></svg>
                                                        </button>

                                                        <Popup id={'exclude-' + i} title={strings.deleteSearchTitle} className='exclude-popup'>

                                                            <p key={'p-exclude-' + i} className='dash-big'>
                                                                {strings.deleteSearchContent}
                                                            </p>

                                                            <div className='bottom-buttons bottom-buttons--left'>

                                                                <Button
                                                                    style='hollow'
                                                                    size='medium'
                                                                    data-dialog-close
                                                                    onClick={() => {
                                                                        handleDeleteSearch(item.id)
                                                                    }}
                                                                >
                                                                    {strings.deleteSearchConfirm}
                                                                </Button>

                                                                <Button
                                                                    style='solid'
                                                                    size='medium'
                                                                    data-dialog-close
                                                                >
                                                                    {strings.deleteSearchCancel}
                                                                </Button>

                                                            </div>

                                                        </Popup>

                                                    </div>

                                                    <ul className='saved-item__filters'>
                                                        {item.filters.map((item, i) => (
                                                            <li key={i}>
                                                                <span className='dash-tag'>
                                                                    {item.text}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>

                                                    <div className='saved-item__bottom'>
                                                        <p>
                                                            {item.searchQuery ? (
                                                                <>
                                                                    {strings.searchedTerm}: "{item.searchQuery}"
                                                                </>
                                                            ) : null}
                                                        </p>

                                                        <Button
                                                            style='solid'
                                                            size='medium'
                                                            onClick={() => {
                                                                (document.getElementById('save-search-textarea-' + i) as HTMLTextAreaElement).value = item?.label || '';
                                                                (document.getElementById('endre-' + i) as HTMLDialogElement).showModal()
                                                                setEditSearch(item)
                                                            }}
                                                        >
                                                            {strings.editSearchButton}
                                                        </Button>

                                                        <Popup id={'endre-' + i} title={strings.editSearchTitle}>

                                                            <p key={'p-endre-textarea-' + i} className='dash-big'>
                                                                {strings.editSearchInputText}
                                                            </p>

                                                            <TextArea
                                                                id={'save-search-textarea-' + i}
                                                                name='Lagre sok'
                                                                placeholder={strings.saveSearchInputPlaceholder}
                                                                defaultValue={editSearch?.label}
                                                                onChange={(e) => {
                                                                    setEditSearch({
                                                                        ...editSearch,
                                                                        label: e.target.value
                                                                    })
                                                                }}
                                                            />

                                                            <p key={'p-endre-input-' + i} className='dash-big'>
                                                                {strings.searchedTerm}
                                                            </p>

                                                            <input
                                                                className='dash-input'
                                                                type='text'
                                                                id='search-query'
                                                                name='Searched term'
                                                                defaultValue={item.searchQuery}
                                                                onChange={(e) => {
                                                                    setEditSearch({
                                                                        ...editSearch,
                                                                        searchQuery: e.target.value
                                                                    })
                                                                }}
                                                            />

                                                            <div className='filters'>

                                                                {
                                                                    editSearch?.filters && editSearch.filters.map((filter, index) =>
                                                                        <button
                                                                            key={index}
                                                                            className='dash-tag'
                                                                            onClick={(e) => {
                                                                                const button = e.target as Element
                                                                                const closestButton = button.closest('button')

                                                                                if (closestButton) {
                                                                                    closestButton.remove()
                                                                                }

                                                                                setEditSearch({
                                                                                    ...editSearch,
                                                                                    filters: editSearch.filters.filter(f => f.id != filter.id)
                                                                                })
                                                                            }}
                                                                        >

                                                                            <span>
                                                                                {filter.text}
                                                                            </span>

                                                                            <span className='clear'>
                                                                                <svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M0.514695 0.514726C0.905219 0.124201 1.53838 0.124201 1.92891 0.514726L8.99998 7.5858L16.0711 0.514726C16.4616 0.124201 17.0947 0.124201 17.4853 0.514726C17.8758 0.90525 17.8758 1.53841 17.4853 1.92894L10.4142 9.00001L17.4853 16.0711C17.8758 16.4616 17.8758 17.0948 17.4853 17.4853C17.0947 17.8758 16.4616 17.8758 16.071 17.4853L8.99998 10.4142L1.92892 17.4853C1.5384 17.8758 0.905233 17.8758 0.514708 17.4853C0.124184 17.0948 0.124184 16.4616 0.514708 16.0711L7.58577 9.00001L0.514695 1.92894C0.124171 1.53841 0.124171 0.90525 0.514695 0.514726Z'></path></svg>
                                                                            </span>

                                                                        </button>
                                                                    )
                                                                }

                                                            </div>

                                                            <div className='bottom-buttons'>

                                                                <Button
                                                                    style='hollow'
                                                                    size='medium'
                                                                    data-dialog-close
                                                                >
                                                                    {strings.editSearchCancel}
                                                                </Button>

                                                                <Button
                                                                    style='solid'
                                                                    size='medium'
                                                                    data-dialog-close
                                                                    onClick={() => handleEditSearch()}
                                                                >
                                                                    {strings.editSearchConfirm}
                                                                </Button>

                                                            </div>

                                                        </Popup>

                                                    </div>

                                                </div>
                                            ))}
                                        </div>
                                    </Popup>

                                </div>
                            </div>

                        </div>

                        {activeFilters.length > 0 && (
                            <div className='active-filters'>

                                <button
                                    onClick={() => {
                                        // clear the active filters
                                        setActiveFilters([])

                                        // uncheck all checkboxes based on the filter IDs
                                        activeFilters.forEach((filter) => {
                                            const checkbox = document.getElementById(filter.id) as HTMLInputElement

                                            if (checkbox) {
                                                checkbox.checked = false
                                            }
                                        })
                                    }}
                                    className='dash-tag dash-tag--active'
                                >

                                    <span>
                                        {strings.clearAllFilters}
                                    </span>

                                    <span className='clear'>
                                        <svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M0.514695 0.514726C0.905219 0.124201 1.53838 0.124201 1.92891 0.514726L8.99998 7.5858L16.0711 0.514726C16.4616 0.124201 17.0947 0.124201 17.4853 0.514726C17.8758 0.90525 17.8758 1.53841 17.4853 1.92894L10.4142 9.00001L17.4853 16.0711C17.8758 16.4616 17.8758 17.0948 17.4853 17.4853C17.0947 17.8758 16.4616 17.8758 16.071 17.4853L8.99998 10.4142L1.92892 17.4853C1.5384 17.8758 0.905233 17.8758 0.514708 17.4853C0.124184 17.0948 0.124184 16.4616 0.514708 16.0711L7.58577 9.00001L0.514695 1.92894C0.124171 1.53841 0.124171 0.90525 0.514695 0.514726Z'></path></svg>
                                    </span>

                                </button>

                                {activeFilters.map((filter, index) => (
                                    <button
                                        key={index}
                                        className='dash-tag'
                                        onClick={() => clearFilter(filter.id, filter.name)}
                                    >

                                        <span>
                                            {filter.text}
                                        </span>

                                        <span className='clear'>
                                            <svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M0.514695 0.514726C0.905219 0.124201 1.53838 0.124201 1.92891 0.514726L8.99998 7.5858L16.0711 0.514726C16.4616 0.124201 17.0947 0.124201 17.4853 0.514726C17.8758 0.90525 17.8758 1.53841 17.4853 1.92894L10.4142 9.00001L17.4853 16.0711C17.8758 16.4616 17.8758 17.0948 17.4853 17.4853C17.0947 17.8758 16.4616 17.8758 16.071 17.4853L8.99998 10.4142L1.92892 17.4853C1.5384 17.8758 0.905233 17.8758 0.514708 17.4853C0.124184 17.0948 0.124184 16.4616 0.514708 16.0711L7.58577 9.00001L0.514695 1.92894C0.124171 1.53841 0.124171 0.90525 0.514695 0.514726Z'></path></svg>
                                        </span>

                                    </button>
                                ))}

                            </div>
                        )}

                        {
                            filters.justShow && (filters.justShow.length > 0) &&
                            <div className='checkboxes'>

                                <h3 className='dash-h4'>
                                    {strings.justShowFilterHeader}
                                </h3>

                                {filters.justShow.map((item, i) => (
                                    <Checkbox
                                        key={i}
                                        id={`just-show-${item.id}`}
                                        name='just-show'
                                        text={item.text}
                                        onChange={(e) =>
                                            handleCheckboxChange(item.id, item.text, 'just-show', e.target.checked)
                                        }
                                    />
                                ))}

                            </div>
                        }

                        <div className='accordions'>

                            <Accordion title='Dato' className='date'>
                                {
                                    domLoaded &&
                                    <ThemeProvider theme={createTheme(nnNO)}>
                                        <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                            adapterLocale='en-gb'
                                        >

                                            <p className='dash-small'>
                                                {strings.dateFilterFrom}
                                            </p>

                                            <div className='date-input'>

                                                <DatePicker
                                                    closeOnSelect={true}
                                                    disableFuture={true}
                                                    minDate={dayjs('2000-01-01')}
                                                    maxDate={dayjs()}
                                                    name='Date Start'
                                                    ref={dateStart}
                                                    value={filterDateStart}
                                                    onChange={(e: any) => {
                                                        handleDateChange(e || null, 'start')
                                                    }}
                                                />

                                                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'><path d='M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l352 0 0 256c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256z' /></svg>

                                            </div>

                                            {filterDateStart && (
                                                <div className='clear'>
                                                    <Button
                                                        style='solid'
                                                        size='small'
                                                        onClick={() => handleDateChange(null, 'start')}
                                                    >
                                                        {strings.dateFilterClear}
                                                    </Button>
                                                </div>
                                            )}

                                            <p className='dash-small'>
                                                {strings.dateFilterTo}
                                            </p>

                                            <div className='date-input'>

                                                <DatePicker
                                                    closeOnSelect={true}
                                                    disableFuture={true}
                                                    minDate={dayjs('2000-01-01')}
                                                    maxDate={dayjs()}
                                                    name='Date End'
                                                    ref={dateEnd}
                                                    value={filterDateEnd}
                                                    onChange={(e: any) => {
                                                        handleDateChange(e || null, 'end')
                                                    }}
                                                />

                                                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'><path d='M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l352 0 0 256c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256z' /></svg>

                                            </div>

                                            {filterDateEnd && (
                                                <div className='clear'>
                                                    <Button
                                                        style='solid'
                                                        size='small'
                                                        onClick={() => handleDateChange(null, 'end')}
                                                    >
                                                        {strings.dateFilterClear}
                                                    </Button>
                                                </div>
                                            )}

                                        </LocalizationProvider>
                                    </ThemeProvider>
                                }

                            </Accordion>

                            {
                                filters.status && (filters.status.length > 0) &&
                                <Accordion title={strings.statusFilterHeader}>
                                    {filters.status.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={`status-${item.id}`}
                                            name='status'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'status', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.packaging && (filters.packaging.length > 0) &&
                                <Accordion title={strings.packagingFilterHeader}>
                                    {filters.packaging.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={`packaging-${item.id}`}
                                            name='packaging'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'packaging', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.taste && (filters.taste.length > 0) &&
                                <Accordion title={strings.tasteFilterHeader}>
                                    {filters.taste.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={`taste-${item.id}`}
                                            name='taste'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'taste', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>

                            }

                            {
                                filters.category && (filters.category.length > 0) &&
                                <Accordion title={strings.categoryFilterHeader}>
                                    {filters.category.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={`category-${item.id}`}
                                            name='category'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'category', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.region && (filters.region.length > 0) &&
                                <Accordion title={strings.regionFilterHeader}>
                                    {filters.region.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={`region-${item.id}`}
                                            name='region'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'region', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.ingredients1 && (filters.ingredients1.length > 0) &&
                                <Accordion title={filters.ingredients1Label}>
                                    {filters.ingredients1.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='ingredient1'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'ingredient1', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.ingredients2 && (filters.ingredients2.length > 0) &&
                                <Accordion title={filters.ingredients2Label}>
                                    {filters.ingredients2.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='ingredient2'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'ingredient2', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                            {
                                filters.ingredients3 && (filters.ingredients3.length > 0) &&
                                <Accordion title={filters.ingredients3Label}>
                                    {filters.ingredients3.map((item, i) => (
                                        <Checkbox
                                            key={i}
                                            id={item.id}
                                            name='ingredient3'
                                            text={item.text}
                                            onChange={(e) =>
                                                handleCheckboxChange(item.id, item.text, 'ingredient3', e.target.checked)
                                            }
                                        />
                                    ))}
                                </Accordion>
                            }

                        </div>

                    </div>
                </div>

                <div className='dashboard-grid-container__right dashboard-grid-container__right--q-idea'>

                    <div className='top-pagination'>

                        <div className='buttons-left'>

                            {
                                filters && (Object.keys(filters).length > 0) &&
                                <>
                                    <Button
                                        style='hollow'
                                        size='medium'
                                        onClick={handleSelectAll}
                                    >
                                        {strings.selectAll}
                                    </Button>

                                    {ideaSelected && (
                                        <>

                                            <Button
                                                style='hollow'
                                                size='medium'
                                                onClick={handleUnselectAll}
                                            >
                                                {strings.unselectAll}
                                            </Button>

                                            <Button
                                                style='solid'
                                                size='medium'
                                                className='edit'
                                                onClick={() => {
                                                    setSelectedIdeas((selectedIdeas) => selectedIdeas.filter(selectedIdea => inEditionContents.indexOf(selectedIdea.id) == -1))
                                                    sendWsMessage('add', selectedIdeas.map(idea => idea.id));
                                                    (document.getElementById('edit-all-ideas') as HTMLDialogElement).showModal()
                                                }}
                                            >
                                                {strings.editContent}
                                            </Button>

                                            <Popup id='edit-all-ideas' title={strings.editContent}
                                                onCloseFn={() => {
                                                    sendWsMessage('remove', (selectedIdeasRef.current || []).map(idea => idea.id));
                                                }}
                                            >
                                                <div className='all-ideas-grid'>

                                                    <div className='grid-left'>
                                                        <ul>
                                                            {selectedIdeas.length > 0 &&
                                                                selectedIdeas.map((selectedIdea, i) => (
                                                                    <li key={i}>

                                                                        <svg width='53' height='53' viewBox='0 0 53 53' xmlns='http://www.w3.org/2000/svg'><path d='M52.5506 7.55048L26.2373 33.8638C25.9092 34.1915 25.4638 34.3762 25.0001 34.3762C24.5363 34.3762 24.0916 34.1921 23.7634 33.8644L14.1162 24.2171L15.884 22.4494L25.0001 31.5655L50.7829 5.78271L52.5506 7.55048Z' /><path d='M38.6459 5.91889C33.905 3.1819 28.3698 2.15097 22.9615 2.99767C17.5532 3.84438 12.5979 6.51765 8.92036 10.5726C5.24282 14.6275 3.0648 19.8196 2.74879 25.2846C2.43279 30.7497 3.99784 36.1582 7.1835 40.61C10.3692 45.0618 14.9833 48.2884 20.258 49.7528C25.5326 51.2173 31.1498 50.8312 36.1745 48.6589C41.1992 46.4866 45.3285 42.659 47.8751 37.8133C50.4218 32.9675 51.2322 27.3957 50.1714 22.0253C50.0599 21.4609 50.4271 20.913 50.9915 20.8015C51.5559 20.69 52.1038 21.0572 52.2153 21.6216C53.3682 27.459 52.4874 33.5153 49.7193 38.7824C46.9512 44.0496 42.4629 48.2099 37.0012 50.5712C31.5396 52.9324 25.434 53.352 19.7006 51.7602C13.9673 50.1685 8.95193 46.6613 5.48926 41.8224C2.02659 36.9835 0.325448 31.1047 0.668936 25.1644C1.01242 19.2241 3.37983 13.5806 7.37716 9.17301C11.3745 4.76547 16.7607 1.85974 22.6393 0.939409C28.5179 0.0190794 34.5344 1.13966 39.6875 4.11465C40.1857 4.40228 40.3564 5.03935 40.0688 5.53758C39.7812 6.03581 39.1441 6.20653 38.6459 5.91889Z' /></svg>

                                                                        <span>
                                                                            {selectedIdea.title}
                                                                        </span>

                                                                    </li>
                                                                ))
                                                            }
                                                        </ul>
                                                    </div>

                                                    <div className='grid-right'>

                                                        <div className='grid-right__selects'>
                                                            {
                                                                filters.status && (filters.status.length > 0) &&
                                                                <>
                                                                    <p className='dash-small label'>
                                                                        {strings.statusFilterHeader}
                                                                    </p>

                                                                    <Select
                                                                        id='mass-edit-status'
                                                                        name='status'
                                                                        defaultValue=''
                                                                    >
                                                                        <option value=''>{strings.chooseOne} {(strings.statusFilterHeader || '').toLowerCase()}</option>
                                                                        {
                                                                            filters.status.map((item, i) =>
                                                                                <option key={i} value={item.id}>{item.text}</option>
                                                                            )
                                                                        }
                                                                    </Select>
                                                                </>
                                                            }

                                                            {
                                                                filters.packaging && (filters.packaging.length > 0) &&
                                                                <>
                                                                    <p className='dash-small label'>
                                                                        {strings.packagingFilterHeader}
                                                                    </p>

                                                                    <Select
                                                                        id='mass-edit-innpakning'
                                                                        name='packaging'
                                                                        defaultValue=''
                                                                    >
                                                                        <option value=''>{strings.chooseOne} {(strings.packagingFilterHeader || '').toLowerCase()}</option>
                                                                        {
                                                                            filters.packaging.map((item, i) =>
                                                                                <option key={i} value={item.id}>{item.text}</option>
                                                                            )
                                                                        }
                                                                    </Select>
                                                                </>
                                                            }

                                                            {
                                                                filters.taste && (filters.taste.length > 0) &&
                                                                <>
                                                                    <p className='dash-small label'>
                                                                        {strings.tasteFilterHeader}
                                                                    </p>

                                                                    <Select
                                                                        id='mass-edit-smak'
                                                                        name='taste'
                                                                        defaultValue=''
                                                                    >
                                                                        <option value=''>{strings.chooseOne} {(strings.tasteFilterHeader || '').toLowerCase()}</option>
                                                                        {
                                                                            filters.taste.map((item, i) =>
                                                                                <option key={i} value={item.id}>{item.text}</option>
                                                                            )
                                                                        }
                                                                    </Select>
                                                                </>
                                                            }

                                                            {
                                                                filters.category && (filters.category.length > 0) &&
                                                                <>
                                                                    <p className='dash-small label'>
                                                                        {strings.categoryFilterHeader}
                                                                    </p>

                                                                    <Select
                                                                        id='mass-edit-kategori'
                                                                        name='category'
                                                                        defaultValue=''
                                                                    >
                                                                        <option value=''>{strings.chooseOne} {(strings.categoryFilterHeader || '').toLowerCase()}</option>
                                                                        {
                                                                            filters.category.map((item, i) =>
                                                                                <option key={i} value={item.id}>{item.text}</option>
                                                                            )
                                                                        }
                                                                    </Select>
                                                                </>

                                                            }

                                                            {
                                                                filters.region && (filters.region.length > 0) &&
                                                                <>
                                                                    <p className='dash-small label'>
                                                                        {strings.regionFilterHeader}
                                                                    </p>

                                                                    <Select
                                                                        id='mass-edit-region'
                                                                        name='region'
                                                                        defaultValue=''
                                                                    >
                                                                        <option value=''>{strings.chooseOne} {(strings.regionFilterHeader || '').toLowerCase()}</option>
                                                                        {
                                                                            filters.region.map((item, i) =>
                                                                                <option key={i} value={item.id}>{item.text}</option>
                                                                            )
                                                                        }
                                                                    </Select>
                                                                </>

                                                            }

                                                        </div>

                                                        <div className='last-buttons'>

                                                            <Button
                                                                style='solid'
                                                                size='medium'
                                                                data-dialog-close
                                                                onClick={() => {
                                                                    handleEditIdea(selectedIdeas.map(selectedIdea => {
                                                                        return {
                                                                            id: selectedIdea.id,
                                                                            message: null,
                                                                            date: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                                                                            userEmail: selectedIdea.user?.email,
                                                                            metadata: {
                                                                                status: (document.getElementById('mass-edit-status') as HTMLInputElement)?.value || '',
                                                                                packaging: (document.getElementById('mass-edit-innpakning') as HTMLInputElement)?.value || '',
                                                                                taste: (document.getElementById('mass-edit-smak') as HTMLInputElement)?.value || '',
                                                                                category: (document.getElementById('mass-edit-kategori') as HTMLInputElement)?.value || '',
                                                                                region: (document.getElementById('mass-edit-region') as HTMLInputElement)?.value || '',
                                                                            }
                                                                        }
                                                                    }))
                                                                }}
                                                            >
                                                                {strings.saveButton}
                                                            </Button>

                                                        </div>

                                                    </div>

                                                </div>
                                            </Popup>

                                        </>
                                    )}
                                </>
                            }


                            <Button
                                style='hollow'
                                size='medium'
                                className='unlock'
                                onClick={() => (document.getElementById('unlock-all-ideas') as HTMLDialogElement).showModal()}
                            >
                                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24px' height='24px'><path d='M 18 1 C 14.67619 1 12 3.6761905 12 7 L 12 8 L 2.9296875 8 C 1.8344913 8 1 8.9455845 1 10 L 1 20 C 1 21.054415 1.8344913 22 2.9296875 22 L 14.070312 22 C 15.16551 22 16 21.054415 16 20 L 16 10 C 16 8.9455845 15.167599 8 14.072266 8 L 14 8 L 14 7 C 14 4.7238095 15.72381 3 18 3 C 20.27619 3 22 4.7238095 22 7 L 22 9 L 24 9 L 24 7 C 24 3.6761905 21.32381 1 18 1 z M 3 10 L 14 10 L 14 20 L 3 20 L 3 10 z M 8.5078125 13 C 7.4078125 13 6.5078125 13.9 6.5078125 15 C 6.5078125 16.1 7.4078125 17 8.5078125 17 C 9.6078125 17 10.507812 16.1 10.507812 15 C 10.507812 13.9 9.6078125 13 8.5078125 13 z' /></svg>

                                <span>
                                    {strings.unlockWsButton}
                                </span>

                            </Button>

                            <Popup id='unlock-all-ideas' title={strings.unlockWsPopupTitle}>

                                <p className='dash-big'>
                                    {strings.unlockWsPopupContent}
                                </p>

                                <div className='bottom-buttons bottom-buttons--left'>

                                    <Button
                                        style='hollow'
                                        size='medium'
                                        data-dialog-close
                                        onClick={() => {
                                            sendWsMessage('clear_all')
                                        }}
                                    >
                                        {strings.unlockWsPopupConfirm}
                                    </Button>

                                    <Button
                                        style='solid'
                                        size='medium'
                                        data-dialog-close
                                    >
                                        {strings.unlockWsPopupCancel}
                                    </Button>

                                </div>

                            </Popup>

                        </div>

                        <div className='pagination'>

                            <span>
                                {totalItems ? ((currentPage - 1) * itemsPerPage + 1) : 0}
                            </span>

                            -

                            <span>
                                {Math.min(currentPage * itemsPerPage, totalItems)}
                            </span>

                            <span className='of'>
                                {strings.paginationOf}
                            </span>

                            <span className='total'>
                                {totalItems}
                            </span>

                        </div>

                    </div>

                    <div className='q-idea-content'>

                        {ideas.map((item, i) => (
                            <div key={i}>
                                <div className={clsx(
                                    'dash-idea',
                                    item.answered && 'dash-idea--responded-to',
                                    item.reported && 'dash-idea--reported',
                                    (inEditionContents.indexOf(item.id) != -1) && 'dash-idea--being-edited',
                                    item.isCampaignForm && 'dash-idea--campaign-form'
                                )}>

                                    <button onClick={() => {
                                        sendWsMessage('add', [item.id]);
                                        (document.getElementById('exclude-idea-' + item.id) as HTMLDialogElement).showModal()
                                    }} className='exclude'>
                                        <svg width='22' height='24' viewBox='0 0 22 24' xmlns='http://www.w3.org/2000/svg'><path d='M21.3125 4.09375H16.1562V2.6875C16.1562 2.0659 15.9093 1.46976 15.4698 1.03022C15.0302 0.59068 14.4341 0.34375 13.8125 0.34375H8.1875C7.5659 0.34375 6.96976 0.59068 6.53022 1.03022C6.09068 1.46976 5.84375 2.0659 5.84375 2.6875V4.09375H0.6875C0.56318 4.09375 0.443951 4.14314 0.356044 4.23104C0.268136 4.31895 0.21875 4.43818 0.21875 4.5625C0.21875 4.68682 0.268136 4.80605 0.356044 4.89396C0.443951 4.98186 0.56318 5.03125 0.6875 5.03125H2.09375V22.375C2.09375 22.748 2.24191 23.1056 2.50563 23.3694C2.76935 23.6331 3.12704 23.7812 3.5 23.7812H18.5C18.873 23.7812 19.2306 23.6331 19.4944 23.3694C19.7581 23.1056 19.9062 22.748 19.9062 22.375V5.03125H21.3125C21.4368 5.03125 21.556 4.98186 21.644 4.89396C21.7319 4.80605 21.7812 4.68682 21.7812 4.5625C21.7812 4.43818 21.7319 4.31895 21.644 4.23104C21.556 4.14314 21.4368 4.09375 21.3125 4.09375ZM6.78125 2.6875C6.78125 2.31454 6.92941 1.95685 7.19313 1.69313C7.45685 1.42941 7.81454 1.28125 8.1875 1.28125H13.8125C14.1855 1.28125 14.5431 1.42941 14.8069 1.69313C15.0706 1.95685 15.2188 2.31454 15.2188 2.6875V4.09375H6.78125V2.6875ZM18.9688 22.375C18.9688 22.4993 18.9194 22.6185 18.8315 22.7065C18.7435 22.7944 18.6243 22.8438 18.5 22.8438H3.5C3.37568 22.8438 3.25645 22.7944 3.16854 22.7065C3.08064 22.6185 3.03125 22.4993 3.03125 22.375V5.03125H18.9688V22.375ZM8.65625 10.1875V17.6875C8.65625 17.8118 8.60686 17.931 8.51896 18.019C8.43105 18.1069 8.31182 18.1562 8.1875 18.1562C8.06318 18.1562 7.94395 18.1069 7.85604 18.019C7.76814 17.931 7.71875 17.8118 7.71875 17.6875V10.1875C7.71875 10.0632 7.76814 9.94395 7.85604 9.85604C7.94395 9.76814 8.06318 9.71875 8.1875 9.71875C8.31182 9.71875 8.43105 9.76814 8.51896 9.85604C8.60686 9.94395 8.65625 10.0632 8.65625 10.1875ZM14.2812 10.1875V17.6875C14.2812 17.8118 14.2319 17.931 14.144 18.019C14.056 18.1069 13.9368 18.1562 13.8125 18.1562C13.6882 18.1562 13.569 18.1069 13.481 18.019C13.3931 17.931 13.3438 17.8118 13.3438 17.6875V10.1875C13.3438 10.0632 13.3931 9.94395 13.481 9.85604C13.569 9.76814 13.6882 9.71875 13.8125 9.71875C13.9368 9.71875 14.056 9.76814 14.144 9.85604C14.2319 9.94395 14.2812 10.0632 14.2812 10.1875Z' /></svg>
                                    </button>

                                    <Button
                                        style='solid'
                                        size='medium'
                                        className='edit'
                                        onClick={() => {
                                            if (document.getElementById('status-' + item.id) as HTMLTextAreaElement) (document.getElementById('status-' + item.id) as HTMLTextAreaElement).value = item.filters.status || '';
                                            if (document.getElementById('innpakning-' + item.id) as HTMLTextAreaElement) (document.getElementById('innpakning-' + item.id) as HTMLTextAreaElement).value = item.filters.packaging || '';
                                            if (document.getElementById('smak-' + item.id) as HTMLTextAreaElement) (document.getElementById('smak-' + item.id) as HTMLTextAreaElement).value = item.filters.taste || '';
                                            if (document.getElementById('kategori-' + item.id) as HTMLTextAreaElement) (document.getElementById('kategori-' + item.id) as HTMLTextAreaElement).value = item.filters.category || '';
                                            if (document.getElementById('region-' + item.id) as HTMLTextAreaElement) (document.getElementById('region-' + item.id) as HTMLTextAreaElement).value = item.filters.region || '';
                                            if (document.getElementById('answer-' + item.id) as HTMLTextAreaElement) (document.getElementById('answer-' + item.id) as HTMLTextAreaElement).value = item.userAnswer || '';
                                            if (document.getElementById('rediger-idea-' + item.id) as HTMLDialogElement) (document.getElementById('rediger-idea-' + item.id) as HTMLDialogElement).showModal()

                                            sendWsMessage('add', [item.id]);
                                        }}
                                    >
                                        {item.isCampaignForm ? strings.detailContent : strings.editContent}
                                    </Button>

                                    <input
                                        type='checkbox'
                                        name={item.title + ' ' + item.id}
                                        id={'idea-' + item.id}
                                        checked={(inEditionContents.indexOf(item.id) == -1) && (selectedIdeas.filter(i => i.id == item.id).length > 0)}
                                        onChange={(e) => handleIdeaSelection(item, e.target.checked)}
                                        disabled={(inEditionContents.indexOf(item.id) != -1)}
                                    />

                                    <label htmlFor={'idea-' + item.id} className='checkbox'>
                                        <svg width='53' height='53' viewBox='0 0 53 53' xmlns='http://www.w3.org/2000/svg'><path d='M52.5506 7.55048L26.2373 33.8638C25.9092 34.1915 25.4638 34.3762 25.0001 34.3762C24.5363 34.3762 24.0916 34.1921 23.7634 33.8644L14.1162 24.2171L15.884 22.4494L25.0001 31.5655L50.7829 5.78271L52.5506 7.55048Z' /><path d='M38.6459 5.91889C33.905 3.1819 28.3698 2.15097 22.9615 2.99767C17.5532 3.84438 12.5979 6.51765 8.92036 10.5726C5.24282 14.6275 3.0648 19.8196 2.74879 25.2846C2.43279 30.7497 3.99784 36.1582 7.1835 40.61C10.3692 45.0618 14.9833 48.2884 20.258 49.7528C25.5326 51.2173 31.1498 50.8312 36.1745 48.6589C41.1992 46.4866 45.3285 42.659 47.8751 37.8133C50.4218 32.9675 51.2322 27.3957 50.1714 22.0253C50.0599 21.4609 50.4271 20.913 50.9915 20.8015C51.5559 20.69 52.1038 21.0572 52.2153 21.6216C53.3682 27.459 52.4874 33.5153 49.7193 38.7824C46.9512 44.0496 42.4629 48.2099 37.0012 50.5712C31.5396 52.9324 25.434 53.352 19.7006 51.7602C13.9673 50.1685 8.95193 46.6613 5.48926 41.8224C2.02659 36.9835 0.325448 31.1047 0.668936 25.1644C1.01242 19.2241 3.37983 13.5806 7.37716 9.17301C11.3745 4.76547 16.7607 1.85974 22.6393 0.939409C28.5179 0.0190794 34.5344 1.13966 39.6875 4.11465C40.1857 4.40228 40.3564 5.03935 40.0688 5.53758C39.7812 6.03581 39.1441 6.20653 38.6459 5.91889Z' /></svg>
                                    </label>

                                    {(inEditionContents.indexOf(item.id) != -1) && (
                                        <div className='editing-status'>
                                            {strings.isEditingLabel}
                                        </div>
                                    )}

                                    <div className='item'>
                                        <div className='tags dash-smallest'>

                                            {item.answered && (
                                                <span className='responded'>
                                                    {strings.isAnsweredLabel}
                                                </span>
                                            )}

                                            {item.reported && (
                                                <div className='reported-group'>
                                                    <span className='reported'>
                                                        {strings.isReportedLabel}
                                                    </span>
                                                    {(type === 'q-idea' && endpoints.unreport) && (
                                                        <Button
                                                            style='hollow'
                                                            size='small'
                                                            className='unreport unreport--inline'
                                                            onClick={() => handleUnreportIdea(item)}
                                                        >
                                                            {strings.unreportButton}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}

                                        </div>

                                        <a
                                            href={item.url}
                                            target='_blank'
                                            className='top-infos'
                                            tabIndex={(inEditionContents.indexOf(item.id) != -1) ? -1 : null}
                                        >

                                            <h3 className='dash-h4 title'>
                                                {item.title}
                                            </h3>

                                            {
                                                !item.hideIcons &&
                                                <div className='icons'>

                                                    <div className='icon likes'>

                                                        <span>
                                                            {item.likes}
                                                        </span>

                                                        <svg width='24' height='22' viewBox='0 0 24 22' xmlns='http://www.w3.org/2000/svg'><path d='M11.9 21.29L11.48 20.94C2.45 13.59 0 11 0 6.8C0 3.3 2.8 0.5 6.3 0.5C9.17 0.5 10.78 2.11 11.9 3.37C13.02 2.11 14.63 0.5 17.5 0.5C21 0.5 23.8 3.3 23.8 6.8C23.8 11 21.35 13.59 12.32 20.94L11.9 21.29ZM6.3 1.9C3.57 1.9 1.4 4.07 1.4 6.8C1.4 10.37 3.64 12.75 11.9 19.47C20.16 12.75 22.4 10.37 22.4 6.8C22.4 4.07 20.23 1.9 17.5 1.9C15.05 1.9 13.72 3.37 12.67 4.56L11.9 5.47L11.13 4.56C10.08 3.37 8.75 1.9 6.3 1.9Z' /></svg>

                                                    </div>

                                                    <div className='icon comments'>

                                                        <span>
                                                            {item.comments}
                                                        </span>

                                                        <svg width='23' height='23' viewBox='0 0 23 23' xmlns='http://www.w3.org/2000/svg'><path d='M6.93196 0.854774C8.98383 0.00486541 11.2416 -0.21751 13.4199 0.21577C15.5981 0.64905 17.599 1.71852 19.1694 3.28895C20.7398 4.85938 21.8093 6.86023 22.2426 9.03847C22.6759 11.2167 22.4535 13.4745 21.6036 15.5264C20.7537 17.5782 19.3144 19.332 17.4678 20.5659C15.6211 21.7998 13.4501 22.4583 11.2292 22.4583C9.46812 22.4583 7.79862 22.052 6.31321 21.3276L0.875758 22.4435C0.635669 22.4927 0.386887 22.4181 0.21358 22.2448C0.0402732 22.0715 -0.0343723 21.8227 0.0148971 21.5826L1.1307 16.1453C0.405217 14.6588 1.22202e-05 12.9912 1.22202e-05 11.2292C1.22202e-05 9.00825 0.658592 6.83721 1.89247 4.99058C3.12635 3.14395 4.8801 1.70468 6.93196 0.854774ZM13.1354 1.64608C11.24 1.26907 9.27543 1.46257 7.49004 2.2021C5.70466 2.94163 4.17866 4.19398 3.10503 5.80079C2.0314 7.40759 1.45835 9.29668 1.45835 11.2292C1.45835 12.8461 1.84925 14.3671 2.54347 15.7089C2.62014 15.8571 2.64367 16.0271 2.61013 16.1906L1.66573 20.7926L6.26777 19.8482C6.43114 19.8147 6.60107 19.8382 6.74921 19.9148C8.09012 20.608 9.61356 21 11.2292 21C13.1617 21 15.0508 20.427 16.6576 19.3533C18.2644 18.2797 19.5167 16.7537 20.2563 14.9683C20.9958 13.1829 21.1893 11.2183 20.8123 9.32298C20.4353 7.42762 19.5047 5.68663 18.1382 4.32015C16.7717 2.95367 15.0307 2.02309 13.1354 1.64608Z' /></svg>

                                                    </div>

                                                </div>
                                            }

                                        </a>

                                        {item.extraField && (
                                            <div className='new-field'>
                                                <p>
                                                    <i>{item.extraField}</i>
                                                </p>
                                            </div>
                                        )}


                                        <div className='middle-infos'>
                                            {
                                                !item.hideImage &&
                                                <div className='image'>
                                                    <Image
                                                        src={item.image || assets.noPhoto}
                                                        alt={item.title}
                                                        width={150}
                                                    />
                                                </div>
                                            }

                                            <div className='infos'>

                                                <p className='date dash-small bold'>
                                                    {dayjs(item.date).format('DD/MM/YYYY')}
                                                </p>

                                                <div className='small-desc'>
                                                    <RichText content={item.isCampaignForm ? item.description : limitCharacters(item.description, 150)} />
                                                </div>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                                <Popup id={'exclude-idea-' + item.id} title={strings.excludePopupTitle} className='exclude-popup'
                                    onCloseFn={() => {
                                        sendWsMessage('remove', [item.id]);
                                    }}
                                >

                                    <p className='dash-big'>
                                        {strings.excludePopupContent}
                                    </p>

                                    <div className='bottom-buttons bottom-buttons--left'>

                                        <Button
                                            style='hollow'
                                            size='medium'
                                            data-dialog-close
                                            onClick={() => {
                                                handleDeleteIdeas([item.id])
                                            }}
                                        >
                                            {strings.excludePopupConfirm}
                                        </Button>

                                        <Button
                                            style='solid'
                                            size='medium'
                                            data-dialog-close
                                            onClick={() => {
                                                sendWsMessage('remove', [item.id]);
                                            }}
                                        >
                                            {strings.excludePopupCancel}
                                        </Button>

                                    </div>

                                </Popup>

                                <Popup id={'rediger-idea-' + item.id}
                                    className={clsx(
                                        'edit-idea-single',
                                        item.isCampaignForm && 'edit-idea-single--campaign-form'
                                    )}
                                    onCloseFn={() => {
                                        sendWsMessage('remove', [item.id]);
                                        setAiReplySelectedSuggestion(null);
                                        setAiReplySuggestions([]);
                                    }}
                                >

                                    <div className='popup-top-infos'>

                                        <a
                                            href={item.url}
                                            target='_blank'
                                            className='title'
                                        >

                                            {
                                                item.url &&
                                                <span className='icon'>
                                                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path d='M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l82.7 0L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3l0 82.7c0 17.7 14.3 32 32 32s32-14.3 32-32l0-160c0-17.7-14.3-32-32-32L320 0zM80 32C35.8 32 0 67.8 0 112L0 432c0 44.2 35.8 80 80 80l320 0c44.2 0 80-35.8 80-80l0-112c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 112c0 8.8-7.2 16-16 16L80 448c-8.8 0-16-7.2-16-16l0-320c0-8.8 7.2-16 16-16l112 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 32z' /></svg>
                                                </span>
                                            }

                                            <span className='dash-h4'>
                                                {item.title}
                                            </span>

                                        </a>

                                        {
                                            !item.hideIcons &&
                                            <div className='icons'>

                                                <div className='icon likes'>

                                                    <span>
                                                        {item.likes}
                                                    </span>

                                                    <svg width='24' height='22' viewBox='0 0 24 22' xmlns='http://www.w3.org/2000/svg'><path d='M11.9 21.29L11.48 20.94C2.45 13.59 0 11 0 6.8C0 3.3 2.8 0.5 6.3 0.5C9.17 0.5 10.78 2.11 11.9 3.37C13.02 2.11 14.63 0.5 17.5 0.5C21 0.5 23.8 3.3 23.8 6.8C23.8 11 21.35 13.59 12.32 20.94L11.9 21.29ZM6.3 1.9C3.57 1.9 1.4 4.07 1.4 6.8C1.4 10.37 3.64 12.75 11.9 19.47C20.16 12.75 22.4 10.37 22.4 6.8C22.4 4.07 20.23 1.9 17.5 1.9C15.05 1.9 13.72 3.37 12.67 4.56L11.9 5.47L11.13 4.56C10.08 3.37 8.75 1.9 6.3 1.9Z' /></svg>

                                                </div>

                                                <div className='icon comments'>

                                                    <span>
                                                        {item.comments}
                                                    </span>

                                                    <svg width='23' height='23' viewBox='0 0 23 23' xmlns='http://www.w3.org/2000/svg'><path d='M6.93196 0.854774C8.98383 0.00486541 11.2416 -0.21751 13.4199 0.21577C15.5981 0.64905 17.599 1.71852 19.1694 3.28895C20.7398 4.85938 21.8093 6.86023 22.2426 9.03847C22.6759 11.2167 22.4535 13.4745 21.6036 15.5264C20.7537 17.5782 19.3144 19.332 17.4678 20.5659C15.6211 21.7998 13.4501 22.4583 11.2292 22.4583C9.46812 22.4583 7.79862 22.052 6.31321 21.3276L0.875758 22.4435C0.635669 22.4927 0.386887 22.4181 0.21358 22.2448C0.0402732 22.0715 -0.0343723 21.8227 0.0148971 21.5826L1.1307 16.1453C0.405217 14.6588 1.22202e-05 12.9912 1.22202e-05 11.2292C1.22202e-05 9.00825 0.658592 6.83721 1.89247 4.99058C3.12635 3.14395 4.8801 1.70468 6.93196 0.854774ZM13.1354 1.64608C11.24 1.26907 9.27543 1.46257 7.49004 2.2021C5.70466 2.94163 4.17866 4.19398 3.10503 5.80079C2.0314 7.40759 1.45835 9.29668 1.45835 11.2292C1.45835 12.8461 1.84925 14.3671 2.54347 15.7089C2.62014 15.8571 2.64367 16.0271 2.61013 16.1906L1.66573 20.7926L6.26777 19.8482C6.43114 19.8147 6.60107 19.8382 6.74921 19.9148C8.09012 20.608 9.61356 21 11.2292 21C13.1617 21 15.0508 20.427 16.6576 19.3533C18.2644 18.2797 19.5167 16.7537 20.2563 14.9683C20.9958 13.1829 21.1893 11.2183 20.8123 9.32298C20.4353 7.42762 19.5047 5.68663 18.1382 4.32015C16.7717 2.95367 15.0307 2.02309 13.1354 1.64608Z' /></svg>

                                                </div>

                                            </div>
                                        }

                                    </div>

                                    {
                                        item.extraField && (
                                            <div className="popup-new-field">
                                                <p className='text-big'>
                                                    <i>{item.extraField}</i>
                                                </p>
                                            </div>
                                        )
                                    }

                                    <div className='popup-middle-infos'>

                                        {
                                            !item.isCampaignForm && item.user &&
                                            <div className='responsible'>
                                                {
                                                    item.user?.name &&
                                                    <p className='name bold'>
                                                        {item.user.name}
                                                    </p>
                                                }

                                                <div className='responsible__infos'>
                                                    {
                                                        item.user?.email &&
                                                        <p className='email'>
                                                            {item.user.email}
                                                        </p>
                                                    }

                                                    {
                                                        item.user?.phone &&
                                                        <p className='phone'>
                                                            {strings.phoneLabel}: <span>{item.user.phone}</span>
                                                        </p>
                                                    }

                                                </div>

                                            </div>
                                        }

                                        {
                                            item.formDetails && Array.isArray(item.formDetails) &&
                                            <div className='responsible'>
                                                {
                                                    item.formDetails.map(formDetail =>
                                                        <p>{formDetail}</p>
                                                    )
                                                }
                                            </div>
                                        }

                                        {
                                            !item.hideImage &&
                                            <div className='image'>
                                                {
                                                    item.image &&
                                                    <a
                                                        href='#'
                                                        className='image__delete'
                                                        onClick={() => {
                                                            (document.getElementById('delete-idea-image-' + i) as HTMLDialogElement).showModal()
                                                        }}
                                                    >
                                                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'><path d='M170.5 51.6L151.5 80l145 0-19-28.4c-1.5-2.2-4-3.6-6.7-3.6l-93.7 0c-2.7 0-5.2 1.3-6.7 3.6zm147-26.6L354.2 80 368 80l48 0 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-8 0 0 304c0 44.2-35.8 80-80 80l-224 0c-44.2 0-80-35.8-80-80l0-304-8 0c-13.3 0-24-10.7-24-24S10.7 80 24 80l8 0 48 0 13.8 0 36.7-55.1C140.9 9.4 158.4 0 177.1 0l93.7 0c18.7 0 36.2 9.4 46.6 24.9zM80 128l0 304c0 17.7 14.3 32 32 32l224 0c17.7 0 32-14.3 32-32l0-304L80 128zm80 64l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208c0-8.8 7.2-16 16-16s16 7.2 16 16z' /></svg>
                                                    </a>
                                                }

                                                <Image
                                                    src={item.image || assets.noPhoto}
                                                    alt={item.title}
                                                    width={200}
                                                />
                                            </div>
                                        }

                                        <div className='details'>

                                            <div className='full-desc'>
                                                <RichText content={item.description} />
                                            </div>

                                            <div className='selects'>
                                                {
                                                    filters.status && (filters.status.length > 0) &&
                                                    <div>

                                                        <p className='dash-small label'>
                                                            {strings.statusFilterHeader}
                                                        </p>

                                                        <Select
                                                            id={'status-' + item.id}
                                                            name={'Status ' + item.id}
                                                            defaultValue={item.filters.status ? item.filters.status : ''}
                                                        >
                                                            <option value=''>{strings.chooseOne} {(strings.statusFilterHeader || '').toLowerCase()}</option>
                                                            {
                                                                filters.status.map((item, i) =>
                                                                    <option key={i} value={item.id}>{item.text}</option>
                                                                )
                                                            }
                                                        </Select>

                                                    </div>
                                                }

                                                {
                                                    filters.packaging && (filters.packaging.length > 0) &&
                                                    <div>

                                                        <p className='dash-small label'>
                                                            {strings.packagingFilterHeader}
                                                        </p>

                                                        <Select
                                                            id={'innpakning-' + item.id}
                                                            name={'Innpakning ' + item.id}
                                                            defaultValue={item.filters.packaging ? item.filters.packaging : ''}
                                                        >
                                                            <option value=''>{strings.chooseOne} {(strings.packagingFilterHeader || '').toLowerCase()}</option>
                                                            {
                                                                filters.packaging.map((item, i) =>
                                                                    <option key={i} value={item.id}>{item.text}</option>
                                                                )
                                                            }
                                                        </Select>

                                                    </div>
                                                }

                                                {
                                                    filters.taste && (filters.taste.length > 0) &&
                                                    <div>

                                                        <p className='dash-small label'>
                                                            {strings.tasteFilterHeader}
                                                        </p>

                                                        <Select
                                                            id={'smak-' + item.id}
                                                            name={'Smak ' + item.id}
                                                            defaultValue={item.filters.taste ? item.filters.taste : ''}
                                                        >
                                                            <option value=''>{strings.chooseOne} {(strings.tasteFilterHeader || '').toLowerCase()}</option>
                                                            {
                                                                filters.taste.map((item, i) =>
                                                                    <option key={i} value={item.id}>{item.text}</option>
                                                                )
                                                            }
                                                        </Select>

                                                    </div>
                                                }

                                                {
                                                    filters.category && (filters.category.length > 0) &&
                                                    <div>

                                                        <p className='dash-small label'>
                                                            {strings.categoryFilterHeader}
                                                        </p>

                                                        <Select
                                                            id={'kategori-' + item.id}
                                                            name={'Kategori ' + item.id}
                                                            defaultValue={item.filters.category ? item.filters.category : ''}
                                                        >
                                                            <option value=''>{strings.chooseOne} {(strings.categoryFilterHeader || '').toLowerCase()}</option>
                                                            {
                                                                filters.category.map((item, i) =>
                                                                    <option key={i} value={item.id}>{item.text}</option>
                                                                )
                                                            }
                                                        </Select>

                                                    </div>
                                                }

                                                {
                                                    filters.region && (filters.region.length > 0) &&
                                                    <div>

                                                        <p className='dash-small label'>
                                                            {strings.regionFilterHeader}
                                                        </p>

                                                        <Select
                                                            id={'region-' + item.id}
                                                            name={'Område ' + item.id}
                                                            defaultValue={item.filters.region ? item.filters.region : ''}
                                                        >
                                                            <option value=''>{strings.chooseOne} {(strings.regionFilterHeader || '').toLowerCase()}</option>
                                                            {
                                                                filters.region.map((item, i) =>
                                                                    <option key={i} value={item.id}>{item.text}</option>
                                                                )
                                                            }
                                                        </Select>

                                                    </div>
                                                }

                                            </div>

                                        </div>

                                    </div>

                                    <div className='popup-bottom-infos'>
                                        {
                                            item.attachments && item.attachments.length > 0 && (
                                                <div className='attachments'>

                                                    <p className='dash-small'>
                                                        Attachments:
                                                    </p>

                                                    <div className='relative'>

                                                        <select
                                                            className='dash-input'
                                                            onChange={(e) => {
                                                                const id = e.target.value;

                                                                for (const group of item.attachments) {
                                                                    if ('items' in group && group.items) {
                                                                        const found = group.items.find(subItem => subItem.id === id)

                                                                        if (found) {
                                                                            window.open(found.url, '_blank')
                                                                            break
                                                                        }
                                                                    } else if (group.id === id && 'url' in group) {
                                                                        window.open(group.url as string, '_blank')
                                                                        break
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <option value=''>
                                                                Attachments (Click to view)
                                                            </option>

                                                            {item.attachments.map((group) => (
                                                                <optgroup key={group.id} label={group.name}>
                                                                    {group.items.map((subItem) => (
                                                                        <option value={subItem.id} key={subItem.id}>
                                                                            {subItem.name}
                                                                        </option>
                                                                    ))}
                                                                </optgroup>
                                                            ))}
                                                        </select>

                                                        <svg width='22' height='11' viewBox='0 0 22 11' xmlns='http://www.w3.org/2000/svg'><path d='M11.8804 10.5936L21.5467 1.88578C21.7081 1.74049 21.7981 1.54745 21.7981 1.34672C21.7981 1.14598 21.7081 0.952951 21.5467 0.807657L21.5358 0.798282C21.4576 0.727602 21.3634 0.671321 21.259 0.632862C21.1547 0.594403 21.0423 0.574571 20.9287 0.574571C20.8151 0.574571 20.7027 0.594403 20.5983 0.632862C20.494 0.671321 20.3998 0.727602 20.3216 0.798282L11.2196 8.99828L2.12135 0.798282C2.04312 0.727602 1.94895 0.671321 1.84459 0.632862C1.74022 0.594403 1.62782 0.574571 1.51425 0.574571C1.40067 0.574571 1.28827 0.594403 1.1839 0.632862C1.07954 0.671321 0.985374 0.727602 0.907146 0.798282L0.896223 0.807657C0.734856 0.952951 0.644835 1.14598 0.644835 1.34672C0.644835 1.54745 0.734856 1.74049 0.896223 1.88578L10.5625 10.5936C10.6475 10.6702 10.7497 10.7311 10.863 10.7728C10.9763 10.8145 11.0982 10.8359 11.2215 10.8359C11.3447 10.8359 11.4667 10.8145 11.5799 10.7728C11.6932 10.7311 11.7954 10.6702 11.8804 10.5936Z' /></svg>

                                                    </div>
                                                </div>
                                            )
                                        }

                                        {
                                            (!item.isCampaignForm || item.user?.newsletterConsent) &&
                                            <TextArea
                                                id={'answer-' + item.id}
                                                name={'Svar fra Q-meieriene' + item.id}
                                                placeholder='Svar'
                                                defaultValue={item.userAnswer || ''}
                                            />
                                        }

                                        {
                                            !item.isCampaignForm &&
                                            <div className='buttons'>

                                                <Button
                                                    style='hollow'
                                                    size='medium'
                                                    data-dialog-close
                                                    onClick={() => {
                                                        handleEditIdea({
                                                            id: item.id,
                                                            message: (document.getElementById('answer-' + item.id) as HTMLInputElement).value || '',
                                                            date: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                                                            userEmail: item.user?.email,
                                                            metadata: {
                                                                status: ((document.getElementById('status-' + item.id) as HTMLInputElement) && (document.getElementById('status-' + item.id) as HTMLInputElement).value) || '',
                                                                packaging: ((document.getElementById('innpakning-' + item.id) as HTMLInputElement) && (document.getElementById('innpakning-' + item.id) as HTMLInputElement).value) || '',
                                                                taste: ((document.getElementById('smak-' + item.id) as HTMLInputElement) && (document.getElementById('smak-' + item.id) as HTMLInputElement).value) || '',
                                                                category: ((document.getElementById('kategori-' + item.id) as HTMLInputElement) && (document.getElementById('kategori-' + item.id) as HTMLInputElement).value) || '',
                                                                region: ((document.getElementById('region-' + item.id) as HTMLInputElement) && (document.getElementById('region-' + item.id) as HTMLInputElement).value) || '',
                                                            }
                                                        })
                                                    }}
                                                >
                                                    {strings.publishButton}
                                                </Button>

                                                {
                                                    item.user?.newsletterConsent &&
                                                    <Button
                                                        style='solid'
                                                        size='medium'
                                                        data-dialog-close
                                                        onClick={() => {
                                                            handleEditIdea({
                                                                id: item.id,
                                                                message: (document.getElementById('answer-' + item.id) as HTMLInputElement)?.value || '',
                                                                date: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                                                                userEmail: item.user?.email,
                                                                metadata: {
                                                                    status: ((document.getElementById('status-' + item.id) as HTMLInputElement) && (document.getElementById('status-' + item.id) as HTMLInputElement).value) || '',
                                                                    packaging: ((document.getElementById('innpakning-' + item.id) as HTMLInputElement) && (document.getElementById('innpakning-' + item.id) as HTMLInputElement).value) || '',
                                                                    taste: ((document.getElementById('smak-' + item.id) as HTMLInputElement) && (document.getElementById('smak-' + item.id) as HTMLInputElement).value) || '',
                                                                    category: ((document.getElementById('kategori-' + item.id) as HTMLInputElement) && (document.getElementById('kategori-' + item.id) as HTMLInputElement).value) || '',
                                                                    region: ((document.getElementById('region-' + item.id) as HTMLInputElement) && (document.getElementById('region-' + item.id) as HTMLInputElement).value) || '',
                                                                }
                                                            }, true)
                                                        }}
                                                    >
                                                        {strings.publishAndSendButton}
                                                    </Button>
                                                }

                                                {
                                                    (type === 'q-idea') &&
                                                    <Button
                                                        style='hollow'
                                                        size='medium'
                                                        onClick={() => {
                                                            (document.getElementById('ai-suggestions-answer-' + item.id) as HTMLDialogElement).showModal()
                                                        }}
                                                    >
                                                        Bruk AI til å foreslå svar
                                                    </Button>
                                                }

                                                <Popup id={'ai-suggestions-answer-' + item.id} title='Bruk AI til å foreslå svar'>
                                                    {
                                                        aiReplyIsLoading &&
                                                        <div className='ai-reply-loading'>
                                                            <svg width='512' height='512' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".05" d="M256 64C150 64 64 150 64 256s86 192 192 192c70.1 0 131.3-37.5 164.9-93.6l.1 .1c-6.9 14.9-1.5 32.8 13 41.2c15.3 8.9 34.9 3.6 43.7-11.7c.2-.3 .4-.6 .5-.9l0 0C434.1 460.1 351.1 512 256 512C114.6 512 0 397.4 0 256S114.6 0 256 0c-17.7 0-32 14.3-32 32s14.3 32 32 32z"/><path d="M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z"/></svg>
                                                        </div>
                                                    }

                                                    {
                                                        !aiReplyIsLoading &&
                                                        <>
                                                            <Select
                                                                id={'ai-suggestions-answer-use-all-admin-replies-' + item.id}
                                                                name={'ai-suggestions-answer-use-all-admin-replies-' + item.id}
                                                                defaultValue={aiReplyUseAllAnswers ? 'true' : ''}
                                                                onChange={(e) =>
                                                                    setAiReplyUseAllAnswers(e.target.value === 'true')
                                                                }
                                                            >
                                                                <option value=''>Use my own replies as a reference</option>
                                                                <option value='true'>Use replies from all admins as a reference</option>
                                                            </Select>
                                                            {
                                                                (aiReplySuggestions.length > 0) &&
                                                                <div className='ai-reply-suggestions'>
                                                                    {
                                                                        aiReplySuggestions.slice(0, 5).map((suggestion, i) => (
                                                                            <div 
                                                                                key={i} 
                                                                                className={clsx('ai-reply-suggestion', { selected: aiReplySelectedSuggestion === suggestion })}
                                                                                onClick={() => {
                                                                                    setAiReplySelectedSuggestion(suggestion);
                                                                                }}
                                                                            >
                                                                                    {suggestion}
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>
                                                            }
                                                        </>
                                                    }

                                                    <div className='buttons'>
                                                        {aiReplySelectedSuggestion && !aiReplyIsLoading && 
                                                            <Button
                                                                style='hollow'
                                                                size='medium'
                                                                disabled={!aiReplySelectedSuggestion || aiReplyIsLoading}
                                                                onClick={() => {
                                                                    (document.getElementById('answer-' + item.id) as HTMLInputElement).value = aiReplySelectedSuggestion;
                                                                    (document.getElementById('ai-suggestions-answer-' + item.id) as HTMLDialogElement).close();
                                                                }}
                                                            >
                                                                Select answer
                                                            </Button>
                                                        }
                                                        <Button
                                                            style='solid'
                                                            size='medium'
                                                            disabled={aiReplyIsLoading}
                                                            onClick={() => {
                                                                handleAiReply(aiReplyUseAllAnswers, item.id)
                                                            }}
                                                        >
                                                            { (aiReplySuggestions.length > 0) ? 'Generate new suggestions' : 'Generate suggestions'}
                                                        </Button>
                                                    </div>
                                                </Popup>

                                            </div>
                                        }

                                        {
                                            item.isCampaignForm && item.user?.newsletterConsent &&
                                            <div className='buttons'>
                                                <Button
                                                    style='hollow'
                                                    size='medium'
                                                    data-dialog-close
                                                    onClick={() => {
                                                        handleEditIdea({
                                                            id: item.id,
                                                            message: (document.getElementById('answer-' + item.id) as HTMLInputElement)?.value || '',
                                                            date: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                                                        })
                                                    }}
                                                >
                                                    Send
                                                </Button>
                                            </div>
                                        }

                                    </div>

                                </Popup>

                                <Popup
                                    id={'delete-idea-image-' + item.id}
                                    title='Slett bildet'
                                    className='exclude-popup'
                                >

                                    <p className='dash-big'>
                                        Er du sikker på at du vil ekskludere dette bildet?
                                    </p>

                                    <div className='bottom-buttons bottom-buttons--left'>

                                        <Button
                                            style='hollow'
                                            size='medium'
                                            data-dialog-close
                                            onClick={() => {
                                                handleDeleteIdeaImage(item)
                                            }}
                                        >
                                            Ja
                                        </Button>

                                        <Button
                                            style='solid'
                                            size='medium'
                                            data-dialog-close
                                        >
                                            Avbryt
                                        </Button>

                                    </div>
                                </Popup>
                            </div>
                        ))}

                        <div className='pagination'>
                            <ul>
                                {getVisiblePages(currentPage, totalPages).map((page, i) => (
                                    <li key={i}>
                                        {page === '...' ? (
                                            <span className='pagination-ellipsis'>
                                                ...
                                            </span>
                                        ) : (
                                            <button
                                                className={clsx({ active: currentPage === page })}
                                                onClick={() => handlePageClick(page)}
                                            >
                                                {page}
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                    </div>

                </div>

            </div>

            <Popup id='dashboard-error-modal' title={errorModal?.title}>
                <p className='dash-big'>{errorModal?.message}</p>
                <div className='bottom-buttons bottom-buttons--left'>
                    <Button
                        style='solid'
                        size='medium'
                        data-dialog-close
                    >
                        {strings.popupModalClose}
                    </Button>
                </div>
            </Popup>

            <Popup id='dashboard-success-modal' title={successModal?.title}
                onCloseFn={successModal?.onCloseFn}
            >
                <p className='dash-big'>{successModal?.message}</p>
                <div className='bottom-buttons bottom-buttons--left'>
                    <Button
                        style='solid'
                        size='medium'
                        data-dialog-close
                        onClick={successModal?.onCloseFn}
                    >
                        {strings.popupModalClose}
                    </Button>
                </div>
            </Popup>
        </main>
    )
}
